// Story 11.12 (ADO-1963), review of PR #1077: opening another entry's order from the product page.
//
// Opening an order is two steps: make its entry the active one (`selectOrderingChoice`, the
// open-cart path the choice screen and the HHT summary use), then go to the order review
// (`/cart`), so the review mounts under that entry and reads its cart. On the product page the
// first step is also an entry change, which the page answers with its own redirect to the new
// entry's destination (`ProductDetail`), and that redirect would replace the navigation to the
// review. So the product page provides its own `OpenOrder` (`ProductPageOpenOrder`): it records
// the open, one-shot, for the location it was made on, and its redirect stands down for exactly
// that change and no other (see `ProductDetail`). Anywhere else no redirect competes, and the
// default does the two steps.

import { createContext, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { selectOrderingChoice } from '../../ordering/orderingSession';

/** Where an open cart is reviewed (Story 8.11), as on the choice screen. */
export const ORDER_REVIEW_ROUTE = '/cart';

/** Make `entryKey` (with `customerAccountCode`) the active entry and open its order review.
 *  Returns false, changing nothing, when the session no longer offers that entry and account. */
export type OpenOrder = (entryKey: string, customerAccountCode: string) => boolean;

/** The product page's own `OpenOrder` (`null`: not on the product page). */
export const ProductPageOpenOrder = createContext<OpenOrder | null>(null);

/** The way to open an order from where the caller is rendered. */
export function useOpenOrder(): OpenOrder {
  const fromProductPage = useContext(ProductPageOpenOrder);
  const navigate = useNavigate();
  const openHere = useCallback<OpenOrder>(
    (entryKey, customerAccountCode) => {
      if (!selectOrderingChoice(entryKey, customerAccountCode)) return false;
      navigate(ORDER_REVIEW_ROUTE);
      return true;
    },
    [navigate],
  );
  return fromProductPage ?? openHere;
}
